import uniq from 'lodash/uniq';
import mergeWith from 'lodash/mergeWith';
import pickBy from 'lodash/pickBy';

const isPrimitive = (value) => typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean';

const isArrayOfPrimitives = (array) => array.reduce((acc, value) => isPrimitive(value) && acc, true);

const isArrayOfArraysOfPrimitives = (parent) =>
    parent.reduce((acc, child) => Array.isArray(child) && isArrayOfPrimitives(child), true);

/**
 * Performs a deep object merge that overwrites arrays
 *
 * The biggest difference from _.merge is the array behavior. _.merge maintains existing keys
 *
 * @param  {Object} object - source object that other sources are merged into
 * @param  {...Object} sources - objects to merge in
 * @return {Object} merged object
 */
export const mergeArrayOverwrite = (object, ...sources) =>
    mergeWith(object, ...sources, (objValue, srcValue) => {
        // Arrays overwrite each other
        if (Array.isArray(objValue)) {
            return srcValue;
        }
        return undefined; // return undefined means do a default merge, object merge.
    });

/**
 * Resolves an entity or key thereof from multiple source values using the default behavior, which
 * performs a deep merge that overwrites arrays
 *
 * @param  {...*} sources - mapping of IDs to entity objects
 * @return {*} the resolved value
 */
export const defaultEntityResolver = (...sources) => {
    const sourcesAreObjects = sources.some((source) => typeof source === 'object');
    if (sourcesAreObjects) {
        // If all sources are arrays, then merge the arrays
        if (isArrayOfArraysOfPrimitives(sources)) {
            return uniq(sources.reduce((acc, arr) => [...acc, ...arr], []));
        }

        return mergeArrayOverwrite({}, ...sources);
    } else {
        return sources.reduce((acc, source) => (source != null ? source : acc));
    }
};

/**
 * Creates an entity merger function that combines any number of entities dictionaries of a single
 * type using the given entity resolution function
 *
 * @param  {function} [resolveEntities] - resolves multiple sources to a single entity
 * @return {function} entity mapper function
 */
export function createEntityMerger(resolveEntities = defaultEntityResolver) {
    return function (...entitySources) {
        /** @var {Object} */
        const mergedEntities = mergeWith({}, ...entitySources, (objValue, srcValue, key, object, source, stack) => {
            if (stack && stack.size) {
                return undefined;
            } else if (objValue === undefined) {
                return srcValue;
            } else if (srcValue === null) {
                return null;
            }
            // If the stack is empty and we're not dealing with an empty starting item, it's time
            // to invoke the resolver on the entities!
            return resolveEntities(objValue, srcValue);
        });

        // Filter out null entities, we want them removed.
        return pickBy(mergedEntities, (entity) => entity !== null);
    };
}

/**
 * Merges entities by using the latest entity in its entirety
 */
export const mergeStrategyOverwrite = createEntityMerger((...entitySources) => {
    if (isArrayOfArraysOfPrimitives(entitySources)) {
        // Assuming all of the entity sources are arrays, keep the last one
        return [...entitySources[entitySources.length - 1]];
    }
    return Object.assign({}, ...entitySources);
});

/**
 * Merges entities by doing a deep merge that overwrites arrays
 */
export const mergeStrategyCombine = createEntityMerger(defaultEntityResolver);

/**
 * Merges entities by doing a deep merge
 */
export const mergeStrategyDeep = createEntityMerger((...entitySources) =>
    mergeWith({}, ...entitySources, () => undefined),
);
